import React from "react";
import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Edition } from "../enums/Edition";

interface GodAllahSwitchProps {
  /** Current selection (Edition.God or Edition.Allah) */
  initialSelectedIndex: Edition;
  /** Notify parent when the selection changes */
  handleEditionChange: (newValue: Edition) => void;
}

// Hoisted styles to avoid re-hashing on every render
const rootBoxSx = { width: 120 } as const;

const groupSx = {
  height: 36,
  "& .MuiToggleButton-root": {
    textTransform: "none",
    px: 2,
    borderColor: "#ccc",
    lineHeight: 1.2,
    fontWeight: 500,
    color: "#333",
    backgroundColor: "#f8f8f8",
    "&:hover": {
      backgroundColor: "#ebebeb",
    },
  },
  "& .Mui-selected": {
    backgroundColor: "#cccccc !important", // medium gray
    color: "#000 !important",
    borderColor: "#999",
    fontWeight: 600,
  },
} as const;

function GodAllahSwitchImpl({ initialSelectedIndex, handleEditionChange }: GodAllahSwitchProps): JSX.Element {
  // Stable handler so parents can memo this component effectively
  const onChange = React.useCallback(
    (_evt: React.MouseEvent<HTMLElement>, newValue: Edition | null) => {
      if (newValue !== null) handleEditionChange(newValue);
    },
    [handleEditionChange],
  );

  return (
    <Box sx={rootBoxSx}>
      <ToggleButtonGroup value={initialSelectedIndex} exclusive onChange={onChange} size="small" aria-label="Edition switch" sx={groupSx}>
        <ToggleButton value={Edition.God} aria-label="God" disableRipple>
          God
        </ToggleButton>
        <ToggleButton value={Edition.Allah} aria-label="Allah" disableRipple>
          Allah
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

// Only re-render if selection or handler changes
export default React.memo(
  GodAllahSwitchImpl,
  (a, b) => a.initialSelectedIndex === b.initialSelectedIndex && a.handleEditionChange === b.handleEditionChange,
);
